/**
 * ReflectedBadge — Sprint 12 Phase C.
 *
 * Small chip rendered next to the confidence score on the investigation
 * detail page when the reflection critic has run on this investigation.
 * Hovering surfaces the ``one_line_lesson`` (or, when null, a generic
 * "no calibration issues found" message).
 *
 * Hidden entirely when ``reflection`` is null — there's nothing to show
 * on running / failed / legacy rows.
 */

import { Chip, Tooltip } from '@mui/material';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';

import type { InvestigationReflection } from '@/types/investigation';

export interface ReflectedBadgeProps {
  reflection: InvestigationReflection | null;
}

function formatTooltip(reflection: InvestigationReflection): string {
  if (reflection.one_line_lesson && reflection.one_line_lesson.trim()) {
    return reflection.one_line_lesson;
  }
  // Clean investigation — the critic ran but found nothing to flag.
  if (
    reflection.schema_passed &&
    reflection.trigger_named &&
    reflection.truncation_acknowledged &&
    !reflection.hypothesised_failure_mode
  ) {
    return 'Calibration critic found no issues with this investigation.';
  }
  // Partial signal — at least one flag failed but the LLM stage didn't
  // produce a lesson. Fall back to listing which flags failed.
  const failed: string[] = [];
  if (!reflection.schema_passed) failed.push('schema');
  if (!reflection.trigger_named) failed.push('trigger');
  if (!reflection.truncation_acknowledged) failed.push('truncation');
  if (reflection.hypothesised_failure_mode) {
    failed.push(`failure mode: ${reflection.hypothesised_failure_mode}`);
  }
  if (failed.length === 0) {
    return 'Calibration critic ran.';
  }
  return `Flagged: ${failed.join(', ')}`;
}

export function ReflectedBadge({ reflection }: ReflectedBadgeProps): JSX.Element | null {
  if (reflection == null) {
    return null;
  }

  const tooltipText = formatTooltip(reflection);

  // Colour choice is informational, not alarming. The badge is meant to
  // tell mohit "the critic looked at this" — operators triage from the
  // tooltip text, not the chip colour.
  return (
    <Tooltip title={tooltipText} arrow>
      <Chip
        size="small"
        variant="outlined"
        icon={<PsychologyAltIcon fontSize="small" />}
        label="reflected"
        data-testid="reflected-badge"
        aria-label={`Reflected: ${tooltipText}`}
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.6875rem',
          height: 22,
          '& .MuiChip-icon': {
            fontSize: 14,
            marginLeft: '6px',
          },
        }}
      />
    </Tooltip>
  );
}
